import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Check, Crown, Zap, Shield, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { stripeAPI } from '../lib/api';

const TIERS = [
  { id: 'free', name: 'Free', price: 0, icon: Zap, features: ['Limited articles per month', 'No ads, ever', 'Newsletter access'], color: '#5B616E' },
  { id: 'basic', name: 'Basic', price: 9.99, icon: Shield, features: ['Unlimited articles', 'Newsletter access', 'Comment access', 'Offline reading'], color: '#1E5A8A', popular: false },
  { id: 'premium', name: 'Premium', price: 19.99, icon: Crown, features: ['Unlimited articles', 'Exclusive content', 'Early access', 'Unlimited bookmarks & offline reading', 'Priority support'], color: '#B89B5E', popular: true },
  { id: 'corporate', name: 'Corporate', price: 99.99, icon: Building2, features: ['Everything in Premium', 'API access', 'Bulk seats', 'Priority support', 'Custom reports'], color: '#4A1D2A' },
];

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentTier, setCurrentTier] = useState('free');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripePrices, setStripePrices] = useState([]);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (user) loadCurrentTier();
    if (sessionId) pollPaymentStatus(sessionId);
    stripeAPI.prices().then(r => {
      setStripePrices(r.data.packages || []);
      setStripeConfigured(r.data.packages?.[0]?.stripe_configured ?? false);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessionId]);

  const loadCurrentTier = async () => {
    try {
      const res = await api.get('/api/subscriptions/user-tier');
      setCurrentTier(res.data.tier || 'free');
    } catch {}
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 8) { toast.error('Payment status check timed out'); return; }
    setChecking(true);
    try {
      const res = await api.get(`/api/payments/status/${sid}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setCurrentTier(res.data.package_id);
        setChecking(false);
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired');
        setChecking(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    }
  };

  const handleSubscribe = async (tierId) => {
    if (!user) { window.location.href = '/login'; return; }
    if (tierId === 'free') return;
    setLoading(true);
    try {
      const res = await api.post('/api/payments/checkout', {
        package_id: tierId,
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally { setLoading(false); }
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16" data-testid="subscription-page">
          {checking && (
            <div className="mb-8 p-4 bg-white rounded-xl border border-[#B89B5E] text-center">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-[#B89B5E]" />
              <p className="text-sm">Verifying your payment...</p>
            </div>
          )}

          <div className="text-center mb-12">
            <h1 className="news-masthead text-4xl font-bold">Choose Your Plan</h1>
            <p className="text-[#5B616E] mt-3 max-w-lg mx-auto">Unlock premium journalism, exclusive content and unlimited reading. No ads, for everyone.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map(tier => {
              const isCurrent = currentTier === tier.id;
              const Icon = tier.icon;
              return (
                <Card key={tier.id} className={`bg-white border-2 relative overflow-hidden transition-shadow hover:shadow-lg ${tier.popular ? 'border-[#B89B5E]' : 'border-[#E7E0D6]'} ${isCurrent ? 'ring-2 ring-[#1F7A5A]' : ''}`} data-testid={`tier-card-${tier.id}`}>
                  {tier.popular && <div className="absolute top-0 right-0 bg-[#B89B5E] text-white text-[10px] font-bold px-3 py-1">MOST POPULAR</div>}
                  <CardContent className="pt-8 pb-6 flex flex-col h-full">
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${tier.color}12` }}>
                        <Icon className="w-6 h-6" style={{ color: tier.color }} />
                      </div>
                      <h3 className="text-lg font-bold">{tier.name}</h3>
                      <div className="mt-2">
                        <span className="text-3xl font-bold">${tier.price}</span>
                        {tier.price > 0 && <span className="text-[#5B616E] text-sm">/mo</span>}
                      </div>
                    </div>
                    <div className="flex-1 space-y-2 mb-6">
                      {tier.features.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-[#1F7A5A] mt-0.5 flex-shrink-0" />
                          <span className="text-[#5B616E]">{f}</span>
                        </div>
                      ))}
                    </div>
                    {isCurrent ? (
                      <Badge className="w-full justify-center py-2 bg-[#1F7A5A] text-white">Current Plan</Badge>
                    ) : tier.price === 0 ? (
                      <Button variant="outline" className="w-full" disabled>Free Forever</Button>
                    ) : (
                      <Button className="w-full bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={() => handleSubscribe(tier.id)} disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Subscribe - $${tier.price}/mo`}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12 text-xs text-[#5B616E]">
            <p>All plans include access to our newsletter. Cancel anytime.</p>
            <p className="mt-1">Payments processed securely via Stripe.</p>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
